import React, { useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { ADMIN_FODBOLDHOLD_ACCESS, MATE } from '../../../../graphql/queries';
import {
  ADD_FODBOLDHOLD_MEMBER,
  DELETE_MATE,
  REMOVE_FODBOLDHOLD_MEMBER,
  SET_FODBOLDHOLD_ADMIN,
  TOGGLE_MATE_ADMIN,
  UPDATE_MATE_PAGE_ACCESS,
} from '../../../../graphql/mutations';
import Button from '../../../../components/Button/Button';
import BackHeader from '../../../../components/BackHeader/BackHeader';
import Card from '../../../../components/Card/Card';
import Loading from '../../../../components/Loading/Loading';
import Notification from '../../../../components/Notification/Notification';
import Spacer from '../../../../components/Spacer/Spacer';
import Switch from '../../../../components/Switch/Switch';
import { ConfirmationModal } from '../../../../components/shared';
import PasswordResetModal from '../PasswordReset/PasswordResetModal';
import './MateDetail.css';
import '../../AdminPanel.css';

const formatDate = (dateString) => {
  if (!dateString) return '–';
  return new Date(dateString).toLocaleDateString('da-DK', {
    weekday: 'short',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const MateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [notification, setNotification] = useState({ open: false, message: '', type: 'info' });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [adminConfirmModal, setAdminConfirmModal] = useState({ isOpen: false, isAdmin: false });
  const [passwordResetModal, setPasswordResetModal] = useState(false);

  const { data, loading, error, refetch } = useQuery(MATE, {
    variables: { id },
    skip: !id,
    onError: (err) => {
      setNotification({ open: true, message: 'Fejl ved hentning af bruger: ' + err.message, type: 'error' });
    }
  });

  const teamsQuery = useQuery(ADMIN_FODBOLDHOLD_ACCESS, {
    fetchPolicy: 'network-only',
    onError: (err) => {
      setNotification({ open: true, message: 'Fejl ved hentning af hold: ' + err.message, type: 'error' });
    }
  });

  const [teamBusy, setTeamBusy] = useState('');

  const [deleteMate] = useMutation(DELETE_MATE, {
    onCompleted: () => {
      setNotification({ open: true, message: 'Bruger slettet!', type: 'success' });
      navigate('/admin');
    },
    onError: (err) => {
      setNotification({ open: true, message: 'Fejl ved sletning: ' + err.message, type: 'error' });
    }
  });

  const [toggleMateAdmin] = useMutation(TOGGLE_MATE_ADMIN, {
    onCompleted: () => {
      setNotification({ open: true, message: 'Admin status opdateret!', type: 'success' });
      refetch();
    },
    onError: (err) => {
      setNotification({ open: true, message: 'Fejl ved opdatering: ' + err.message, type: 'error' });
    }
  });

  const [updateMatePageAccess] = useMutation(UPDATE_MATE_PAGE_ACCESS, {
    onCompleted: () => {
      setNotification({ open: true, message: 'Side-adgange opdateret!', type: 'success' });
      refetch();
    },
    onError: (err) => {
      setNotification({ open: true, message: 'Fejl ved opdatering af side-adgange: ' + err.message, type: 'error' });
    }
  });

  const mate = data?.mate;

  const handleBack = useCallback(() => {
    navigate('/admin'); // TODO: Gå tilbage til listen af brugere
  }, [navigate]);

  const handleDelete = useCallback(() => {
    setDeleteConfirmModal(true);
  }, []);

  const confirmDelete = useCallback(() => {
    if (id) {
      deleteMate({ variables: { id } });
    }
    setDeleteConfirmModal(false);
  }, [id, deleteMate]);

  const handleToggleAdmin = useCallback(() => {
    if (mate) {
      setAdminConfirmModal({ isOpen: true, isAdmin: mate.role !== 'admin' });
    }
  }, [mate]);

  const confirmToggleAdmin = useCallback(() => {
    if (id && adminConfirmModal.isAdmin !== undefined) {
      toggleMateAdmin({ variables: { id, isAdmin: adminConfirmModal.isAdmin } });
    }
    setAdminConfirmModal({ isOpen: false, isAdmin: false });
  }, [id, adminConfirmModal.isAdmin, toggleMateAdmin]);

  const handlePasswordReset = useCallback(() => {
    setPasswordResetModal(true);
  }, []);

  const closePasswordResetModal = useCallback(() => {
    setPasswordResetModal(false);
  }, []);

  const closeNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  const [addFodboldholdMember] = useMutation(ADD_FODBOLDHOLD_MEMBER);
  const [removeFodboldholdMember] = useMutation(REMOVE_FODBOLDHOLD_MEMBER);
  const [setFodboldholdAdmin] = useMutation(SET_FODBOLDHOLD_ADMIN);

  const handleToggleTeam = useCallback(async (teamId, checked) => {
    if (!id || teamBusy) return;
    setTeamBusy(teamId);
    try {
      const mutation = checked ? addFodboldholdMember : removeFodboldholdMember;
      await mutation({ variables: { teamId, mateId: id } });
      await teamsQuery.refetch();
      setNotification({
        open: true,
        message: checked ? 'Brugeren er med på holdet.' : 'Brugeren er fjernet fra holdet.',
        type: 'success',
      });
    } catch (err) {
      setNotification({ open: true, message: err.message || 'Kunne ikke opdatere holdet.', type: 'error' });
    } finally {
      setTeamBusy('');
    }
  }, [addFodboldholdMember, id, removeFodboldholdMember, teamBusy, teamsQuery]);

  const handleToggleHoldAdmin = useCallback(async (teamId, checked) => {
    if (!id || teamBusy) return;
    setTeamBusy(teamId);
    try {
      await setFodboldholdAdmin({ variables: { teamId, mateId: id, isAdmin: checked } });
      await teamsQuery.refetch();
      setNotification({
        open: true,
        message: checked ? 'Brugeren er hold-admin.' : 'Hold-admin er fjernet.',
        type: 'success',
      });
    } catch (err) {
      setNotification({ open: true, message: err.message || 'Kunne ikke opdatere hold-admin.', type: 'error' });
    } finally {
      setTeamBusy('');
    }
  }, [id, setFodboldholdAdmin, teamBusy, teamsQuery]);

  const handleTogglePageAccess = useCallback((pageKey, checked) => {
    if (!id || !mate) return;

    const currentPageAccess = mate.pageAccess || [];
    const nextPageAccess = checked
      ? [...new Set([...currentPageAccess, pageKey])]
      : currentPageAccess.filter((page) => page !== pageKey);

    updateMatePageAccess({
      variables: {
        id,
        pageAccess: nextPageAccess
      }
    });
  }, [id, mate, updateMatePageAccess]);

  if (loading && !mate) {
    return (
      <div className="mate-detail mate-detail-container admin-theme admin-mate-page">
        <Loading text="Henter bruger..." />
      </div>
    );
  }

  if (error || (!loading && !mate)) {
    return (
      <div className="mate-detail mate-detail-container admin-theme admin-mate-page">
        <BackHeader
          theme="mate"
          eyebrow="Mate Management"
          title="Bruger ikke fundet"
          subtitle="Brugeren kunne ikke findes eller der opstod en fejl."
          onBack={handleBack}
          backLabel="Tilbage til Mate Management"
        />
      </div>
    );
  }

  const mateName = mate.name || 'Unnamed';
  const initials = mateName.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  const isAdmin = mate.role === 'admin';
  const pageAccess = mate.pageAccess || [];
  const teams = [...(teamsQuery.data?.allFodboldhold || [])].sort((a, b) => a.name.localeCompare(b.name, 'da'));

  return (
    <div className="mate-detail mate-detail-container admin-theme admin-mate-page">
      <BackHeader
        theme="mate"
        eyebrow="Mate Management"
        title={mateName}
        subtitle={isAdmin ? 'Administrator' : 'Mate'}
        onBack={handleBack}
        backLabel="Tilbage til Mate Management"
      />

      <div className="mate-detail-content">
          <div className="mate-detail-wrapper">
            
          <Spacer size="20px" />
            <div className="mate-detail-section-cards">
              <Card className="mate-detail-section-card" padding="medium" hover={false}>
                <div className="mate-detail-form mate-detail-form-row">
                  <div className="mate-detail-form-row-item">
                    <div className="mate-detail-form-row-item-data">
                    <label>Email</label>
                    <span className="mate-detail-value">{mate.email || '–'}</span>
                    </div>
                  </div>
                  <div className="mate-detail-form-row-item">
                    <div className="mate-detail-form-row-item-data">
                    <label>Rolle</label>
                    <span className="mate-detail-value">{mate.role === 'admin' ? 'Administrator' : 'Mate'}</span>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="mate-detail-section-card" padding="medium" hover={false}>
                <h2 className="mate-detail-section-title">Adgang</h2>

                <div className="mate-detail-section-description">
                  Giv brugeren adgang til følgende spil:
                </div>

                <div className="mate-detail-form-row-item-data"> <label>Yatzy</label>
                  <span className="mate-detail-value">
                    <Switch
                      checked={pageAccess.includes('yatzy')}
                      onChange={(checked) => handleTogglePageAccess('yatzy', checked)}
                      color="primary"
                    />
                  </span>
                </div>
                
                <div className="mate-detail-form-row-item-data"> 
                  <label>Champions League</label>
                  <span className="mate-detail-value">
                    <Switch
                      checked={pageAccess.includes('championsLeague')}
                      onChange={(checked) => handleTogglePageAccess('championsLeague', checked)}
                      color="primary"
                    />
                  </span>
                </div>

                <div className="mate-detail-form-row-item-data">
                  <label>Landskamp</label>
                  <span className="mate-detail-value">
                    <Switch
                      checked={pageAccess.includes('landskamp')}
                      onChange={(checked) => handleTogglePageAccess('landskamp', checked)}
                      color="primary"
                    />
                  </span>
                </div>

                <div className="mate-detail-form-row-item-data">
                  <label>Kampdag</label>
                  <span className="mate-detail-value">
                    <Switch
                      checked={pageAccess.includes('kampdag')}
                      onChange={(checked) => handleTogglePageAccess('kampdag', checked)}
                      color="primary"
                    />
                  </span>
                </div>

                <div className="mate-detail-form-row-item-data">
                  <label>World Cup 2026</label>
                  <span className="mate-detail-value">
                    <Switch
                      checked={pageAccess.includes('worldCup2026')}
                      onChange={(checked) => handleTogglePageAccess('worldCup2026', checked)}
                      color="primary"
                    />
                  </span>
                </div>
              </Card>

              <Card className="mate-detail-section-card" padding="medium" hover={false}>
                <h2 className="mate-detail-section-title">Hold</h2>
                <div className="mate-detail-section-description">
                  Giv brugeren adgang til et hold, og gør personen til hold-admin.
                </div>
                {teamsQuery.loading && teams.length === 0 ? (
                  <p className="mate-detail-section-description">Henter hold…</p>
                ) : null}
                {!teamsQuery.loading && teams.length === 0 ? (
                  <p className="mate-detail-section-description">Der er ingen hold endnu.</p>
                ) : null}
                {teams.map((team) => {
                  const member = (team.members || []).find((item) => item.mateId === id);
                  const onTeam = Boolean(member);
                  return (
                    <div className="mate-detail-form-row-item" key={team.id}> 
                    <div className="mate-detail-form-row-item-data" key={team.id}>
                      <label>{team.name}</label>
                      <span className="mate-detail-value mate-detail-team-controls">
                        <span>
                          Adgang
                          <Switch
                            checked={onTeam}
                            disabled={Boolean(teamBusy)}
                            onChange={(checked) => handleToggleTeam(team.id, checked)}
                            color="primary"
                            ariaLabel={`${team.name} adgang`}
                          />
                        </span>
                        <span>
                          Hold-admin
                          <Switch
                            checked={Boolean(member?.isAdmin)}
                            disabled={!onTeam || Boolean(teamBusy)}
                            onChange={(checked) => handleToggleHoldAdmin(team.id, checked)}
                            color="primary"
                            ariaLabel={`${team.name} hold-admin`}
                          />
                        </span>
                      </span>
                    </div>
                    </div>
                  );
                })}
              </Card>

              <Card className="mate-detail-section-card" padding="medium" hover={false}>
              <div className="mate-detail-section-description">
                  Giv brugeren admin rettigheder, så han kan administrere brugere og sider.
                </div>
                <div className="mate-detail-form-row-item-data">
                  <label>Admin rettigheder</label>
                  <span className="mate-detail-value">
                    <Switch
                      checked={mate.role === 'admin'}
                      onChange={handleToggleAdmin}
                      color="primary"
                    />
                  </span>
                </div>
              </Card>

              <Card className="mate-detail-section-card-actions" padding="medium" hover={false}>

              <div className="mate-detail-form-row-item-data">
                <span className="mate-detail-value">
                  <Button
                    title="Nulstil password"
                    onClick={handlePasswordReset}
                    variant="outlined"
                    color="primary"
                  />
                </span>
              </div>

              <div className="mate-detail-form-row-item-data">
                <span className="mate-detail-value">
                  <Button
                    title="Slet bruger"
                    onClick={handleDelete}
                    variant="outlined"
                    color="danger"
                  />
                </span>
              </div>
              
              </Card>
            </div>
          </div>   
      </div>

      <Notification
        open={notification.open}
        onClose={closeNotification}
        message={notification.message}
        type={notification.type}
      />

      <ConfirmationModal
        isOpen={deleteConfirmModal}
        onClose={() => setDeleteConfirmModal(false)}
        onConfirm={confirmDelete}
        title="Slet bruger"
        message="Er du sikker på at du vil slette denne bruger? Denne handling kan ikke fortrydes."
        confirmText="Slet"
        cancelText="Annuller"
        confirmButtonType="danger"
      />

      <ConfirmationModal
        isOpen={adminConfirmModal.isOpen}
        onClose={() => setAdminConfirmModal({ isOpen: false, isAdmin: false })}
        onConfirm={confirmToggleAdmin}
        title={adminConfirmModal.isAdmin ? 'Giv admin rettigheder' : 'Fjern admin rettigheder'}
        message={`Er du sikker på at du vil ${adminConfirmModal.isAdmin ? 'give' : 'fjerne'} admin rettigheder?`}
        confirmText={adminConfirmModal.isAdmin ? 'Giv admin' : 'Fjern admin'}
        cancelText="Annuller"
        confirmButtonType={adminConfirmModal.isAdmin ? 'primary' : 'danger'}
      />

      <PasswordResetModal
        isOpen={passwordResetModal}
        onClose={closePasswordResetModal}
        mateId={id}
      />
    </div>
  );
};

export default MateDetail;
